import type { ExamId } from "./types";

export interface LessonSection {
  heading: string;
  /** Hỗ trợ: đoạn văn (cách nhau bởi dòng trống), danh sách "- ", `code`, **đậm**. */
  body: string;
}

export interface Lesson {
  id: string;
  stage: number;
  exam: ExamId;
  title: string;
  minutes: number;
  objectives: string[];
  sections: LessonSection[];
  labIds: string[];
  quizIds: string[];
}

export interface Stage {
  id: number;
  title: string;
  subtitle: string;
  exam: ExamId;
}

export const STAGES: Stage[] = [
  { id: 0, title: "Nhập môn", subtitle: "Chưa từng dùng Claude? Bắt đầu ở đây.", exam: "ccao-f" },
  { id: 1, title: "Người dùng thành thạo", subtitle: "Mục tiêu: Associate (CCAO-F)", exam: "ccao-f" },
  { id: 2, title: "Developer", subtitle: "Mục tiêu: Developer (CCDV-F)", exam: "ccdv-f" },
  { id: 3, title: "Architect", subtitle: "Mục tiêu: Architect Foundations (CCAR-F)", exam: "ccar-f" },
  { id: 4, title: "Enterprise Architect", subtitle: "Mục tiêu: Architect Professional (CCAR-P)", exam: "ccar-p" },
];

export const LESSONS: Lesson[] = [
  // ── Stage 0 ────────────────────────────────────────────────
  {
    id: "l-what-is-claude", stage: 0, exam: "ccao-f", minutes: 8,
    title: "Claude là gì và hệ sinh thái sản phẩm",
    objectives: ["Phân biệt các tầng model", "Biết khi nào dùng claude.ai, Claude Code, Cowork hay API"],
    sections: [
      { heading: "Claude là một họ mô hình", body: "Claude là họ mô hình ngôn ngữ lớn của Anthropic, được phát hành theo các tầng:\n\n- **Haiku** — nhanh và rẻ nhất, cho tác vụ khối lượng lớn.\n- **Sonnet** — cân bằng năng lực và tốc độ, lựa chọn mặc định cho coding và agent hằng ngày.\n- **Opus** — suy luận sâu cho tác vụ phức tạp; tầng cao nhất hiện có thêm **Fable**.\n\nChọn model theo yêu cầu chất lượng trước, rồi tối ưu chi phí và độ trễ." },
      { heading: "Các bề mặt sản phẩm", body: "- **claude.ai / app** — trò chuyện, Projects, Artifacts, connectors.\n- **Claude Cowork** — giao tác vụ nhiều bước trên máy tính cho người không lập trình.\n- **Claude Code** — agent lập trình trong terminal/IDE.\n- **Claude API (Claude Platform)** — tích hợp Claude vào sản phẩm của bạn; cũng có trên Amazon Bedrock, Google Vertex AI, Microsoft Foundry." },
      { heading: "Lộ trình chứng chỉ", body: "Bốn kỳ thi từ thấp đến cao: **Associate** (người dùng nghiệp vụ, không code) → **Developer** (xây ứng dụng trên API) → **Architect Foundations** (thiết kế hệ thống agent) → **Architect Professional** (quản trị ở quy mô doanh nghiệp). Mỗi kỳ 120 phút, thang 100–1000, đạt từ 720." },
    ],
    labIds: ["lab-choose-model"],
    quizIds: ["ao-s1", "ao-s2"],
  },
  {
    id: "l-prompt-basics", stage: 0, exam: "ccao-f", minutes: 12,
    title: "Prompt căn bản: rõ ràng, có ngữ cảnh, có ví dụ",
    objectives: ["Viết prompt có ngữ cảnh đầy đủ", "Dùng ví dụ và thẻ XML", "Chỉ định định dạng đầu ra"],
    sections: [
      { heading: "Nguyên tắc vàng", body: "Hãy coi Claude như một đồng nghiệp rất giỏi nhưng **mới vào làm**: không biết bối cảnh công ty, người đọc, hay tiêu chuẩn của bạn. Nếu một người mới đọc prompt và bối rối, Claude cũng vậy.\n\nMột prompt tốt thường có: vai trò/bối cảnh, mục tiêu, dữ liệu đầu vào, ràng buộc, định dạng đầu ra — và **lý do** đằng sau các ràng buộc." },
      { heading: "Ví dụ (few-shot)", body: "Ví dụ là cách mạnh nhất để truyền đạt định dạng và cách xử lý ca khó. Dùng 3–5 ví dụ **đa dạng**, gồm cả ca biên, bọc trong `<example>`." },
      { heading: "Cấu trúc bằng thẻ XML", body: "Tách dữ liệu khỏi chỉ dẫn: `<document>…</document>`, `<order_info>…</order_info>`. Với tài liệu dài: đặt tài liệu ở **đầu**, câu hỏi ở **cuối**.\n\n- Nói điều bạn muốn thay vì chỉ điều không muốn.\n- Chữ IN HOA và \"TUYỆT ĐỐI\" không thay thế được chỉ dẫn rõ ràng." },
    ],
    labIds: ["lab-prompt-rewrite"],
    quizIds: ["ao-p1", "ao-p2", "ao-p3", "ao-p4"],
  },
  {
    id: "l-responsible", stage: 0, exam: "ccao-f", minutes: 10,
    title: "Đánh giá đầu ra & dùng AI có trách nhiệm",
    objectives: ["Nhận diện hallucination", "Áp dụng khung 4D", "Xử lý dữ liệu nhạy cảm đúng cách"],
    sections: [
      { heading: "Hallucination", body: "Mô hình có thể tạo thông tin nghe hợp lý nhưng sai — thường rất cụ thể (tên, số liệu, trích dẫn). Giảm thiểu bằng cách:\n\n- Cho phép Claude nói \"không biết\".\n- Yêu cầu trích dẫn nguyên văn từ tài liệu.\n- Kiểm chứng độc lập — hỏi lại chính model không phải là kiểm chứng." },
      { heading: "Khung AI Fluency 4D", body: "- **Delegation** — quyết định việc gì giao cho AI.\n- **Description** — mô tả rõ yêu cầu.\n- **Discernment** — đánh giá đầu ra và quá trình.\n- **Diligence** — trách nhiệm, minh bạch, an toàn. Con người chịu trách nhiệm cuối cùng." },
      { heading: "Dữ liệu nhạy cảm", body: "Chỉ dùng công cụ được tổ chức phê duyệt, đưa dữ liệu tối thiểu cần thiết, ẩn danh khi có thể. Không bao giờ dán mật khẩu hay bí mật vào prompt." },
    ],
    labIds: [],
    quizIds: ["ao-e1", "ao-e3", "ao-g1", "ao-g3"],
  },
  // ── Stage 1 ────────────────────────────────────────────────
  {
    id: "l-workflows", stage: 1, exam: "ccao-f", minutes: 10,
    title: "Đưa Claude vào quy trình làm việc",
    objectives: ["Dùng Projects, Artifacts, Styles", "Kết nối công cụ bằng connectors", "Biết khi nào dùng Cowork"],
    sections: [
      { heading: "Đúng tính năng cho đúng việc", body: "- **Projects** — tri thức nền + chỉ dẫn dùng chung cho một mảng công việc.\n- **Preferences** — chỉ dẫn cá nhân áp dụng mọi cuộc trò chuyện.\n- **Styles** — chuyển nhanh phong cách trả lời.\n- **Artifacts** — nội dung độc lập để lặp chỉnh sửa và chia sẻ.\n- **Connectors** — truy cập Gmail, Calendar, Drive… qua cơ chế ủy quyền (dựa trên MCP)." },
      { heading: "Xử lý sự cố thường gặp", body: "- Kết quả chung chung → thiếu ngữ cảnh.\n- Sai định dạng → mô tả + ví dụ định dạng.\n- Quên yêu cầu sau hội thoại dài → phiên mới với bản tóm tắt, hoặc đưa vào project instructions.\n- Trả lời dựa trên tài liệu cũ → dọn dẹp project knowledge." },
    ],
    labIds: [],
    quizIds: ["ao-w1", "ao-w2", "ao-c1", "ao-t1"],
  },
  // ── Stage 2 ────────────────────────────────────────────────
  {
    id: "l-messages-api", stage: 2, exam: "ccdv-f", minutes: 12,
    title: "Messages API căn bản",
    objectives: ["Hiểu cấu trúc request/response", "Xử lý stop_reason", "Retry đúng cách"],
    sections: [
      { heading: "Một endpoint cho mọi thứ", body: "Mọi thứ đi qua `POST /v1/messages`: `model`, `max_tokens`, `system` (top-level), `messages` (lượt user/assistant), `tools`. API **stateless** — bạn tự gửi lại lịch sử mỗi lượt." },
      { heading: "stop_reason", body: "- `end_turn` — xong tự nhiên.\n- `max_tokens` — bị cắt, đầu ra có thể dang dở.\n- `tool_use` — Claude muốn gọi tool.\n- `pause_turn` — server tool tạm dừng lượt dài, gửi lại để tiếp tục.\n- `refusal` — bị từ chối.\n\nLuôn kiểm tra stop_reason trước khi dùng nội dung." },
      { heading: "Lỗi và retry", body: "429 (rate limit), 5xx, lỗi mạng → retry với exponential backoff, tôn trọng `retry-after`. 400/401/404 → sửa request, không retry. SDK chính thức đã tự retry mặc định 2 lần. Dùng **streaming** cho UI chat và cho request có `max_tokens` lớn." },
    ],
    labIds: [],
    quizIds: ["dv-a1", "dv-a2", "dv-a3", "dv-a4"],
  },
  {
    id: "l-tool-use", stage: 2, exam: "ccdv-f", minutes: 15,
    title: "Tool use và vòng lặp agent",
    objectives: ["Định nghĩa tool tốt", "Xử lý tool_use / tool_result", "Hiểu Tool Runner vs Agent SDK"],
    sections: [
      { heading: "Luồng gọi tool", body: "Bạn khai báo tool (name, description, input_schema). Claude trả `tool_use` → **ứng dụng của bạn** thực thi → gửi `tool_result` (đúng `tool_use_id`) trong tin nhắn user → Claude tiếp tục. Lặp tới khi `end_turn`." },
      { heading: "Viết tool cho agent", body: "- Description là \"prompt\" của tool: làm gì, trả về gì, khi nào dùng/không dùng.\n- Mọi tham số có mô tả và định dạng.\n- `strict: true` + `additionalProperties: false` để input luôn đúng schema.\n- Lỗi: `is_error: true` với thông điệp hữu ích.\n- Nhiều tool_use song song → mọi tool_result trong **một** tin nhắn user." },
      { heading: "Chọn harness", body: "- **Manual loop** — bạn tự viết vòng lặp.\n- **Tool Runner** (trong Anthropic SDK) — SDK chạy vòng lặp cho tool bạn định nghĩa.\n- **Claude Agent SDK** — harness của Claude Code: tool tích hợp, hooks, subagents; bạn tự host.\n- **Managed Agents** — Anthropic host cả vòng lặp và sandbox." },
    ],
    labIds: ["lab-tool-loop", "lab-tool-def", "lab-parallel-results"],
    quizIds: ["dv-g1", "dv-g2", "dv-t1", "dv-t2", "dv-t3"],
  },
  {
    id: "l-cost", stage: 2, exam: "ccdv-f", minutes: 12,
    title: "Tối ưu chi phí & độ trễ",
    objectives: ["Thiết kế prompt caching đúng", "Biết khi nào dùng Batches", "Điều chỉnh effort"],
    sections: [
      { heading: "Prompt caching", body: "Cache khớp **tiền tố từng byte** theo thứ tự `tools → system → messages`. Đặt `cache_control` ở ranh giới của phần ổn định; phần thay đổi đặt sau.\n\n- TTL 5 phút (mặc định) hoặc 1 giờ.\n- Ghi: 1,25× (5 phút) / 2× (1 giờ); đọc ≈ 0,1× giá input.\n- Tối đa 4 breakpoint. Kiểm chứng bằng `usage.cache_read_input_tokens`." },
      { heading: "Message Batches", body: "Giảm 50% chi phí cho tác vụ không cần thời gian thực: ≤ 100.000 request/256 MB mỗi batch, phần lớn xong < 1 giờ (tối đa 24 giờ), kết quả lưu 29 ngày, ghép theo `custom_id`." },
      { heading: "Effort và thinking", body: "Model hiện đại dùng adaptive thinking; `output_config.effort` (low → max) là đòn bẩy chính cho chất lượng/chi phí. Đo chi phí trên **mỗi tác vụ hoàn thành**, không phải mỗi request." },
    ],
    labIds: ["lab-cache-bug"],
    quizIds: ["dv-m1", "dv-m2", "dv-m3", "dv-p3"],
  },
  {
    id: "l-mcp", stage: 2, exam: "ccdv-f", minutes: 10,
    title: "Model Context Protocol (MCP)",
    objectives: ["Hiểu kiến trúc host–client–server", "Phân biệt tools/resources/prompts", "Chọn transport"],
    sections: [
      { heading: "MCP là gì", body: "Giao thức mở chuẩn hóa cách ứng dụng AI kết nối với công cụ và dữ liệu. Viết một MCP server → dùng được ở nhiều client: Claude Code, claude.ai, agent tùy chỉnh." },
      { heading: "Primitive và transport", body: "- **Tools** — model gọi.\n- **Resources** — dữ liệu ứng dụng đọc.\n- **Prompts** — mẫu prompt người dùng chọn.\n\nTransport: **stdio** (server cục bộ, tiến trình con) và **Streamable HTTP** (server từ xa, thường kèm OAuth)." },
      { heading: "Trong Claude Code", body: "Cấu hình chia sẻ cho team bằng `.mcp.json` ở gốc repo; bí mật qua biến môi trường `${TOKEN}` — không commit token." },
    ],
    labIds: [],
    quizIds: ["dv-t4", "dv-t5", "af-dp4"],
  },
  {
    id: "l-claude-code-101", stage: 2, exam: "ccdv-f", minutes: 10,
    title: "Claude Code nhập môn",
    objectives: ["Dùng CLAUDE.md", "Chạy headless trong CI", "Phân biệt chỉ dẫn và cơ chế tất định"],
    sections: [
      { heading: "Bộ nhớ dự án", body: "`CLAUDE.md` được nạp tự động: lệnh build/test, quy ước code, kiến trúc. `/init` tạo bản đầu tiên. Giữ ngắn gọn, cụ thể, cập nhật thường xuyên." },
      { heading: "Headless", body: "`claude -p \"…\" --output-format json` chạy một lần rồi thoát — dùng cho script và CI." },
      { heading: "Prompt vs hook", body: "CLAUDE.md là **gợi ý** (xác suất). Ràng buộc bắt buộc → **hooks** (PreToolUse có thể chặn) hoặc **permission rules** — cơ chế tất định do harness thực thi." },
    ],
    labIds: ["lab-ci-job"],
    quizIds: ["dv-c1", "dv-c2", "dv-c3"],
  },
  // ── Stage 3 ────────────────────────────────────────────────
  {
    id: "l-agent-arch", stage: 3, exam: "ccar-f", minutes: 15,
    title: "Kiến trúc agent & đa agent",
    objectives: ["Chọn workflow hay agent", "Thiết kế coordinator–subagent", "Truyền ngữ cảnh tường minh"],
    sections: [
      { heading: "Bắt đầu đơn giản", body: "Một lần gọi → workflow (prompt chaining, routing, parallelization, orchestrator–workers, evaluator–optimizer) → agent chỉ khi tác vụ mở, khó xác định trước, giá trị đủ lớn và lỗi khôi phục được." },
      { heading: "Coordinator – subagent", body: "- Subagent **không** thấy lịch sử của coordinator — prompt giao việc phải đầy đủ: mục tiêu, ngữ cảnh, tiêu chí, định dạng trả về, cách báo lỗi.\n- Chạy song song bằng nhiều lời gọi trong cùng một response.\n- Mỗi subagent chỉ nhận bộ tool nhỏ cho vai trò của nó.\n- Subagent trả kết quả cô đọng; lỗi được báo có cấu trúc, không \"nuốt\" thành kết quả rỗng." },
      { heading: "Quy tắc nghiệp vụ", body: "Ràng buộc có hậu quả (tài chính, bảo mật) → thực thi bằng code/hook. Prompt giảm tỉ lệ lỗi; hook đảm bảo 0%." },
    ],
    labIds: ["lab-subagent-brief"],
    quizIds: ["af-re1", "af-re2", "af-re4", "af-re5", "af-su1"],
  },
  {
    id: "l-cc-advanced", stage: 3, exam: "ccar-f", minutes: 12,
    title: "Cấu hình Claude Code nâng cao",
    objectives: ["Phân cấp CLAUDE.md", "Rules theo đường dẫn", "Skills, plan mode, CI/CD"],
    sections: [
      { heading: "Phân cấp", body: "- Managed policy (tổ chức) — bắt buộc.\n- `~/.claude/CLAUDE.md` — sở thích cá nhân.\n- `./CLAUDE.md` — chuẩn team, commit vào git.\n- `.claude/rules/*.md` với frontmatter `paths` — nạp khi làm việc với file khớp glob." },
      { heading: "Skills", body: "`.claude/skills/<tên>/SKILL.md` với frontmatter `name`, `description`. `context: fork` chạy trong ngữ cảnh tách biệt; `allowed-tools` giới hạn quyền." },
      { heading: "Plan mode & CI", body: "Plan mode cho thay đổi lớn/mơ hồ; thực thi trực tiếp cho sửa nhỏ rõ ràng. Trong CI: `-p`, output JSON, giới hạn tool, phiên review **độc lập** với phiên sinh code, tiêu chí rõ ràng để giảm false positive." },
    ],
    labIds: ["lab-config-placement"],
    quizIds: ["af-cg1", "af-cg2", "af-cg3", "af-cg4", "af-ci2"],
  },
  {
    id: "l-structured", stage: 3, exam: "ccar-f", minutes: 12,
    title: "Structured output & trích xuất dữ liệu",
    objectives: ["Thiết kế schema chống hallucination", "Vòng validate–retry", "Batch vs đồng bộ"],
    sections: [
      { heading: "Schema là hợp đồng", body: "- Trường có thể vắng mặt → **nullable**.\n- Enum → thêm `\"other\"` + trường detail.\n- Kiểu dữ liệu đúng (number cho tiền).\n- Schema đảm bảo **cú pháp**, không đảm bảo **ngữ nghĩa** — vẫn validate nghiệp vụ (ví dụ tổng dòng = tổng tiền)." },
      { heading: "Validate – retry", body: "Gửi lại lỗi **cụ thể** để model sửa (sai định dạng, đọc nhầm). Nếu thông tin không có trong nguồn, retry vô ích — trả null và đánh dấu cho người xem." },
      { heading: "Few-shot cho ca mơ hồ", body: "Tài liệu đa dạng bố cục → vài ví dụ minh họa ranh giới (ví dụ ngày ký vs ngày hiệu lực). Khối lượng lớn, không gấp → Batches; luồng chặn người dùng → API đồng bộ." },
    ],
    labIds: ["lab-extraction-schema"],
    quizIds: ["af-ex1", "af-ex2", "af-ex4", "af-ci4"],
  },
  {
    id: "l-context", stage: 3, exam: "ccar-f", minutes: 10,
    title: "Quản lý context & độ tin cậy",
    objectives: ["Giữ dữ kiện quan trọng", "Escalation đúng lúc", "Khám phá codebase lớn"],
    sections: [
      { heading: "Context là tài nguyên hữu hạn", body: "- Tóm tắt lũy tiến làm mất dữ kiện giao dịch → giữ khối **case facts** nguyên văn.\n- Đọc nhiều → giao cho subagent, chỉ nhận tóm tắt có trích dẫn file:line.\n- Ghi phát hiện ra file scratchpad để bền vững qua compaction/phiên mới.\n- Resume phiên khi file đã đổi → báo cho Claude biết hoặc bắt đầu mới với tóm tắt." },
      { heading: "Escalation", body: "Đáng tin: khách yêu cầu người thật · vượt thẩm quyền / khoảng trống chính sách · không tiến triển. **Không** dùng: confidence tự báo, phân tích cảm xúc." },
      { heading: "Provenance", body: "Mỗi claim gắn nguồn và ngày; mâu thuẫn được nêu rõ thay vì tự chọn." },
    ],
    labIds: ["lab-escalation"],
    quizIds: ["af-su2", "af-su4", "af-dp3", "af-re3"],
  },
  // ── Stage 4 ────────────────────────────────────────────────
  {
    id: "l-governance", stage: 4, exam: "ccar-p", minutes: 12,
    title: "Governance & rủi ro ở quy mô doanh nghiệp",
    objectives: ["Phân tầng kiểm soát hành động", "Managed settings", "Yêu cầu dữ liệu & pháp lý"],
    sections: [
      { heading: "Mức tự chủ theo rủi ro", body: "Tự động ca rủi ro thấp, dễ hoàn tác; human-in-the-loop cho hành động không thể hoàn tác hoặc ảnh hưởng bên ngoài; cấm hành động không có lý do nghiệp vụ. Mở rộng dần dựa trên eval và audit." },
      { heading: "Thực thi chính sách", body: "Claude Code: **managed settings** có độ ưu tiên cao nhất, người dùng không ghi đè. Thứ tự: managed > dòng lệnh > local project > shared project > user." },
      { heading: "Dữ liệu và tích hợp", body: "Đối chiếu yêu cầu lưu giữ dữ liệu, vùng địa lý, nền tảng (API, Bedrock, Vertex, Foundry) với tính năng cần dùng. Không dùng secret tĩnh dài hạn khi có thể dùng workload identity federation." },
    ],
    labIds: ["lab-governance"],
    quizIds: ["ap-g1", "ap-g2", "ap-g3", "ap-i2"],
  },
  {
    id: "l-lifecycle", stage: 4, exam: "ccar-p", minutes: 12,
    title: "Eval, tối ưu & vòng đời giải pháp",
    objectives: ["Xây eval đáng tin", "Nâng cấp model an toàn", "Đo ROI và nhân rộng"],
    sections: [
      { heading: "Eval", body: "- Tiêu chí thành công đo được, tập test đại diện, chấm bằng code khi có thể.\n- LLM-as-judge cần rubric và hiệu chỉnh với nhãn người.\n- Tách train/validation/test để tránh overfit khi hill-climb.\n- Giám sát liên tục sau triển khai." },
      { heading: "Vòng đời", body: "Migration guide → eval → tinh chỉnh prompt/effort → canary → rollout có rollback. Theo dõi lịch deprecation." },
      { heading: "Stakeholder & enablement", body: "Báo cáo bằng chỉ số nghiệp vụ so với baseline. Chuẩn hóa qua plugin/skill dùng chung, template cấu hình, champion nội bộ." },
    ],
    labIds: ["lab-model-upgrade"],
    quizIds: ["ap-e1", "ap-e2", "ap-s1", "ap-s2", "ap-n1"],
  },
];

export const LESSON_BY_ID = new Map(LESSONS.map((l) => [l.id, l]));
